import type { FunctionComponent } from "react"

import { clsx } from "clsx"

import { NEW_SIGN_IN_FLOW, NEW_SIGN_IN_FLOW_VARIANTS } from "@/modules/careerCenter/constants/flags"
import { useCareerCenterFetch } from "@/modules/careerCenter/hooks/useCareerCenterFetch"
import { isWwwCareerCenter } from "@/modules/careerCenter/isWwwCareerCenter"
import { isLinkActive } from "@/modules/headerFooterFO/Header/helpers/isLinkActive"
import { LoginButton } from "@/modules/headerFooterFO/Header/LoginUser/LoginButton"
import { useTranslation } from "@/modules/i18n/components/useTranslation"
import { AVAILABLE_LOCALES } from "@/modules/i18n/constants"
import { useLocale } from "@/modules/locales/useLocale"
import { Link } from "@/modules/routing/components/Link"
import { useHref } from "@/modules/routing/hooks/useHref"
import { useFlag } from "@/modules/unleash/hooks/useFlag"
import { useCurrentUserFetch } from "@/modules/user/client/hooks/useCurrentUserFetch"

import { CareerCenter } from "./CareerCenter"
import { ADVICE_ALLOWED_LOCALES } from "./constants"
import { DropdownCommunity } from "./DropdownCommunity"

import styles from "./Nav.module.css"

export const Nav: FunctionComponent = () => {
  const { t } = useTranslation(["module_header_fo"])
  const href = useHref()
  const locale = useLocale()
  const { dataCurrentUser } = useCurrentUserFetch()
  const { dataCareerCenter } = useCareerCenterFetch()
  const isNewSignInFlowEnabled = useFlag(NEW_SIGN_IN_FLOW, NEW_SIGN_IN_FLOW_VARIANTS.ENABLED)

  const isWwwCC = isWwwCareerCenter(dataCareerCenter)
  const isAdviceEnabled =
    !!dataCareerCenter?.careerCenterConf.modules.hasAdvices &&
    ((!isWwwCC && AVAILABLE_LOCALES.includes("en")) || (isWwwCC && ADVICE_ALLOWED_LOCALES.includes(locale)))

  return (
    <ul className={styles["app-Nav"]}>
      {/* Link to Homepage */}
      {!!dataCurrentUser?.attributes.isStudent && (
        <li className={clsx(styles["app-Nav__item"], styles["app-Nav__item--dashboard"])}>
          <Link
            className={clsx(styles["app-Nav__link"], {
              [styles["app-Nav__link--active"]]: isLinkActive(href, `/${locale}/dashboard`),
            })}
            href={`/${locale}/dashboard`}
          >
            {!!dataCurrentUser?.attributes.hasCommunityModule && !!dataCareerCenter?.intranet
              ? t("module_header_fo.nav_dashboard_short")
              : t("module_header_fo.nav_dashboard")}
          </Link>
        </li>
      )}

      {/* Companies */}
      <li className={clsx(styles["app-Nav__item"], styles["app-Nav__item--companies"])}>
        <a
          className={clsx(styles["app-Nav__link"], {
            [styles["app-Nav__link--active"]]: isLinkActive(href, `/${locale}/companies`),
          })}
          href={`/${locale}/companies`}
        >
          {!!dataCurrentUser?.attributes.hasCommunityModule && !!dataCareerCenter?.intranet
            ? t("module_header_fo.nav_companies_short")
            : t("module_header_fo.nav_companies")}
        </a>
      </li>

      {/* Community */}
      {!!dataCurrentUser?.attributes.hasCommunityModule && (
        <li className={clsx(styles["app-Nav__item"], styles["app-Nav__item--community"])}>
          <DropdownCommunity />
        </li>
      )}

      {/* Offers */}
      {!!dataCareerCenter?.careerCenterConf.modules.hasJobOffers && (
        <li className={clsx(styles["app-Nav__item"], styles["app-Nav__item--offers"])}>
          <Link
            className={clsx(styles["app-Nav__link"], {
              [styles["app-Nav__link--active"]]: isLinkActive(href, `/${locale}/job-offers`),
            })}
            href={`/${locale}/job-offers`}
          >
            {!!dataCurrentUser?.attributes.hasCommunityModule && !!dataCareerCenter?.intranet
              ? t("module_header_fo.nav_offers_short")
              : t("module_header_fo.nav_offers")}
          </Link>
        </li>
      )}

      {/* New job_roles  */}
      {!!dataCurrentUser?.attributes.showJobRoles &&
        !!dataCareerCenter?.careerCenterConf.modules.hasGenericPositions && (
          <li className={clsx(styles["app-Nav__item"], styles["app-Nav__item--jobRoles"])}>
            <Link
              className={clsx(styles["app-Nav__link"], {
                [styles["app-Nav__link--active"]]: isLinkActive(href, `/${locale}/job-roles`),
              })}
              href={`/${locale}/job-roles`}
            >
              {t("module_header_fo.nav_jobs_roles")}
            </Link>
          </li>
        )}

      {/* Advices  */}
      {isAdviceEnabled && (
        <li className={clsx(styles["app-Nav__item"], styles["app-Nav__item--advices"])}>
          <a
            className={clsx(styles["app-Nav__link"], {
              [styles["app-Nav__link--active"]]: isLinkActive(href, `/${locale}/advices`),
            })}
            href={`/${locale}/advices`}
          >
            {t("module_header_fo.nav_advices")}
          </a>
        </li>
      )}

      {/* Events  */}
      {!!dataCareerCenter?.careerCenterConf.modules.hasEvents && (
        <li className={clsx(styles["app-Nav__item"], styles["app-Nav__item--events"])}>
          <a
            className={clsx(styles["app-Nav__link"], {
              [styles["app-Nav__link--active"]]: isLinkActive(href, `/${locale}/events`),
            })}
            href={`/${locale}/events`}
          >
            {t("module_header_fo.nav_events")}
          </a>
        </li>
      )}

      {/* Login  */}
      {isNewSignInFlowEnabled && !dataCurrentUser?.isLogged && (
        <li className={clsx(styles["app-Nav__item"], styles["app-Nav__item--login"])}>
          <LoginButton
            careerCenterName={dataCareerCenter?.name || ""}
            isWwwCareerCenter={isWwwCC}
            label={t("module_header_fo.login_button.label")}
            uiPosition="nav"
          />
        </li>
      )}

      {/* CareerCenter nav */}
      <CareerCenter />
    </ul>
  )
}
